export interface LogoutSessionUser {
  email?: string;
  is_adviser?: boolean;
}

export const ACCOUNT_SWITCHER_STORAGE_KEY = 'accountSwitcher'
export const ACCOUNT_DATA_PREFIX = 'accountData_'

const SESSION_STORAGE_KEYS = [
  'userData',
  'userVersion',
  'lastActivity',
  'sso',
  'sso_email',
  'sso_provider_id',
  'sso_logout_url',
  'idToken',
  'addingAccount',
  'switchToEmail',
  'accountSwitchInProgress',
  'authSessionId',
  'authLoginAttemptId',
  'pageBeforeLogin',
]

const SESSION_CACHE_PREFIXES = [
  'happily:teams-view',
  'happily.people.primaryMetrics',
]

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  return window.localStorage
}

export const normalizeAccountEmail = (email: string = ''): string =>
  String(email || '').toLowerCase().trim()

export const getAccountDataKey = (email: string): string =>
  ACCOUNT_DATA_PREFIX + normalizeAccountEmail(email)

const storageKeys = (storage: Storage): string[] => {
  const keys: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key) {
      keys.push(key)
    }
  }
  return keys
}

const removeKeysByPrefix = (storage: Storage, prefix: string) => {
  storageKeys(storage)
    .filter((key) => key.indexOf(prefix) === 0)
    .forEach((key) => storage.removeItem(key))
}

export const removeAllStoredAccountData = () => {
  const storage = getStorage()
  if (!storage) return

  removeKeysByPrefix(storage, ACCOUNT_DATA_PREFIX)
}

export const clearStoredAccountSwitcher = () => {
  const storage = getStorage()
  if (!storage) return

  storage.removeItem(ACCOUNT_SWITCHER_STORAGE_KEY)
  removeAllStoredAccountData()
}

export const removeStoredAccountSwitcherAccount = (email: string) => {
  const storage = getStorage()
  const normalizedEmail = normalizeAccountEmail(email)
  if (!storage || !normalizedEmail) return

  storage.removeItem(getAccountDataKey(normalizedEmail))

  try {
    const stored = storage.getItem(ACCOUNT_SWITCHER_STORAGE_KEY)
    if (!stored) return

    const data = JSON.parse(stored)
    const accounts = Array.isArray(data.accounts) ? data.accounts : []
    const nextAccounts = accounts.filter((account: any) =>
      normalizeAccountEmail(account && account.email) !== normalizedEmail
    )

    if (nextAccounts.length === 0) {
      storage.removeItem(ACCOUNT_SWITCHER_STORAGE_KEY)
      return
    }

    storage.setItem(ACCOUNT_SWITCHER_STORAGE_KEY, JSON.stringify({
      ...data,
      accounts: nextAccounts,
      activeEmail: normalizeAccountEmail(data.activeEmail) === normalizedEmail
        ? ''
        : (data.activeEmail || ''),
    }))
  } catch (error) {
    storage.removeItem(ACCOUNT_SWITCHER_STORAGE_KEY)
  }
}

export const clearSessionLocalStorage = (_sessionUser?: LogoutSessionUser | null) => {
  const storage = getStorage()
  if (!storage) return

  SESSION_STORAGE_KEYS.forEach((key) => storage.removeItem(key))
  SESSION_CACHE_PREFIXES.forEach((prefix) => removeKeysByPrefix(storage, prefix))

  // Account switcher cleanup is handled explicitly by accountSwitcher/handleLogout.
}
